import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { hasUrlCredentials, isValidHttpHeaderValue, validateUrl } from '@insio/validation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { GetWebhookConfigDocument, SaveWebhookConfigDocument } from '@/graphql';

interface WebhookConfig {
  isActive: boolean;
  url: string;
  headerName: string;
  headerValue: string;
}

/** Masked value shown in place of a header value the server already holds. */
const MASKED_HEADER_VALUE = '••••••••••••••••••••••••';

const HEADER_VALUE_INVALID_KEY = 'errors.headerValueInvalid';
const HEADER_VALUE_TOO_LONG_KEY = 'errors.headerValueTooLong';

/** Longest header value the server stores, after trimming. */
const HEADER_VALUE_MAX_LENGTH = 500;

/**
 * Maps a backend save-error message to a webhooks `errors.*` translation key.
 * Mirrors the REASON strings in `@insio/validation`'s `url-safeguard.ts` and the webhook service.
 */
function webhookSaveErrorKey(message: string): string {
  if (/private, loopback, or reserved ip|internal host/i.test(message)) {
    return 'errors.urlNotAllowed';
  }
  if (/user name or password/i.test(message)) {
    return 'errors.urlCredentials';
  }
  if (/valid http or https/i.test(message)) {
    return 'errors.urlInvalid';
  }
  if (/printable ascii/i.test(message)) {
    return HEADER_VALUE_INVALID_KEY;
  }
  if (/header value must be at most/i.test(message)) {
    return HEADER_VALUE_TOO_LONG_KEY;
  }
  return 'errors.saveError';
}

/** The `errors.*` key for a non-empty URL the form refuses, or null when it is fine. */
function urlErrorKey(url: string): string | null {
  const validation = validateUrl(url);
  if (!validation.isValid) {
    return validation.errorKey ? `errors.${validation.errorKey}` : 'errors.urlInvalid';
  }
  return hasUrlCredentials(url) ? 'errors.urlCredentials' : null;
}

/** The `errors.*` key for a typed header value the form refuses, or null; empty means no header. */
function headerValueErrorKey(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed.length > HEADER_VALUE_MAX_LENGTH) return HEADER_VALUE_TOO_LONG_KEY;
  return isValidHttpHeaderValue(trimmed) ? null : HEADER_VALUE_INVALID_KEY;
}

interface ServerState {
  url: string;
  headerName: string;
  hasAuthHeader: boolean;
  isActive: boolean;
}

export function useWebhooksSettings() {
  const t = useFeatureTranslation('systemSettings.webhooks');
  const { showSuccess, showError } = useToastHelpers();

  const [config, setConfig] = useState<WebhookConfig>({
    isActive: false,
    url: '',
    headerName: '',
    headerValue: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [headerValueError, setHeaderValueError] = useState<string | null>(null);
  const [savedUrl, setSavedUrl] = useState<string>('');
  const [tokenFromServer, setTokenFromServer] = useState(false);
  const [clearToken, setClearToken] = useState(false);
  const [clearTokenName, setClearTokenName] = useState(false);
  const [serverState, setServerState] = useState<ServerState>({
    url: '',
    headerName: '',
    hasAuthHeader: false,
    isActive: false
  });

  const { data, loading, error, refetch } = useQuery(GetWebhookConfigDocument);

  const [saveWebhookConfig] = useMutation(SaveWebhookConfigDocument, {
    refetchQueries: [{ query: GetWebhookConfigDocument }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (!data) return;

    if (data.webhookConfig) {
      const hasHeader = data.webhookConfig.hasAuthHeader;
      const url = data.webhookConfig.url ?? '';
      const headerName = data.webhookConfig.authHeaderName ?? '';
      const isActive = data.webhookConfig.isEnabled;
      setConfig({
        isActive,
        url,
        headerName,
        headerValue: hasHeader ? MASKED_HEADER_VALUE : ''
      });
      setHeaderValueError(null);
      setSavedUrl(url);
      setTokenFromServer(hasHeader);
      setClearToken(false);
      setClearTokenName(false);
      setServerState({ url, headerName, hasAuthHeader: hasHeader, isActive });
    } else {
      setConfig({ isActive: false, url: '', headerName: '', headerValue: '' });
      setSavedUrl('');
      setTokenFromServer(false);
      setClearToken(false);
      setClearTokenName(false);
      setUrlError(null);
      setHeaderValueError(null);
      setServerState({ url: '', headerName: '', hasAuthHeader: false, isActive: false });
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      showError(t('errors.loadError') + '.');
    }
  }, [error, showError, t]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setConfig(prev => ({ ...prev, url: newUrl }));
    if (newUrl && newUrl.trim()) {
      const errorKey = urlErrorKey(newUrl);
      setUrlError(errorKey ? t(errorKey) : null);
    } else {
      setUrlError(null);
    }
  };

  const handleClearUrl = () => {
    setConfig(prev => ({ ...prev, url: '' }));
    setSavedUrl('');
    setUrlError(null);
  };

  const handleBearerTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfig(prev => ({ ...prev, headerValue: e.target.value }));
    const errorKey = headerValueErrorKey(e.target.value);
    setHeaderValueError(errorKey ? t(errorKey) : null);
    if (e.target.value) {
      setClearToken(false);
    }
  };

  const handleClearBearerToken = () => {
    setConfig(prev => ({ ...prev, headerValue: '' }));
    setHeaderValueError(null);
    setTokenFromServer(false);
    setClearToken(true);
  };

  const handleBearerTokenNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    const nextHeaderValue = tokenFromServer ? '' : config.headerValue;
    const errorKey = headerValueErrorKey(nextHeaderValue);
    setHeaderValueError(errorKey ? t(errorKey) : null);
    setConfig(prev => ({
      ...prev,
      headerName: newName,
      headerValue: tokenFromServer ? '' : prev.headerValue
    }));
    if (tokenFromServer) {
      setTokenFromServer(false);
    }
    if (newName) {
      setClearTokenName(false);
    }
  };

  const handleClearBearerTokenName = () => {
    setConfig(prev => ({
      ...prev,
      headerName: '',
      headerValue: tokenFromServer ? '' : prev.headerValue
    }));
    if (tokenFromServer) {
      setTokenFromServer(false);
    }
    setHeaderValueError(null);
    setClearTokenName(true);
  };

  /** Saves the form; resolves true only when the server accepted the save. */
  const handleSave = async (isEnabledOverride?: boolean): Promise<boolean> => {
    const isActive = isEnabledOverride !== undefined ? isEnabledOverride : config.isActive;
    const hasUrl = config.url.trim().length > 0;

    if (isActive) {
      if (!hasUrl) {
        const errorMessage = t('errors.urlRequiredForActivation');
        setUrlError(errorMessage);
        showError(errorMessage + '.');
        return false;
      }
    }

    if (hasUrl) {
      const errorKey = urlErrorKey(config.url);
      if (errorKey) {
        const errorMessage = t(errorKey);
        setUrlError(errorMessage);
        showError(errorMessage + '.');
        return false;
      }
    }

    // The masked server value and a deleted header are never sent, so they are never checked.
    const headerValueTyped = !tokenFromServer && !clearToken && !clearTokenName;
    const headerErrorKey = headerValueTyped ? headerValueErrorKey(config.headerValue) : null;
    if (headerErrorKey) {
      const errorMessage = t(headerErrorKey);
      setHeaderValueError(errorMessage);
      showError(errorMessage + '.');
      return false;
    }

    try {
      setIsSaving(true);

      const tokenInput =
        clearToken || clearTokenName
          ? { clearAuthHeader: true }
          : tokenFromServer
            ? {}
            : config.headerValue && config.headerName
              ? { headerName: config.headerName, headerValue: config.headerValue }
              : config.headerName
                ? { headerName: config.headerName }
                : {};

      await saveWebhookConfig({
        variables: {
          input: {
            url: config.url.trim() === '' ? '' : config.url.trim(),
            isEnabled: isActive,
            ...tokenInput
          }
        },
        // Global default is errorPolicy:'all', which resolves (never throws) on a backend
        // rejection — override so save failures reach the catch instead of a false success.
        errorPolicy: 'none'
      });

      showSuccess(t('success.saveSuccess') + '.');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      const errorKey = webhookSaveErrorKey(message);
      const errorText = t(errorKey);
      if (errorKey === HEADER_VALUE_INVALID_KEY || errorKey === HEADER_VALUE_TOO_LONG_KEY) {
        setHeaderValueError(errorText);
      } else {
        setUrlError(errorText);
      }
      showError(errorText + '.');
      try {
        await refetch();
      } catch {
        // The refusal is already shown; a failed reload must not hide it from the caller.
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    const newIsActive = !config.isActive;
    if (newIsActive && !savedUrl.trim()) return;
    setConfig(prev => ({ ...prev, isActive: newIsActive }));
    setIsTogglingActive(true);
    try {
      const saved = await handleSave(newIsActive);
      if (!saved) {
        setConfig(prev => ({ ...prev, isActive: !newIsActive }));
      }
    } finally {
      setIsTogglingActive(false);
    }
  };

  const isFormDirty =
    config.url !== serverState.url ||
    config.headerName !== serverState.headerName ||
    clearToken ||
    clearTokenName ||
    (!tokenFromServer && config.headerValue !== '');

  const canSave = !urlError && !headerValueError && isFormDirty;
  const activationLocked = !savedUrl.trim();

  return {
    t,
    config,
    loading,
    isSaving,
    isTogglingActive,
    urlError,
    headerValueError,
    tokenFromServer,
    canSave,
    activationLocked,
    handleUrlChange,
    handleClearUrl,
    handleBearerTokenChange,
    handleClearBearerToken,
    handleBearerTokenNameChange,
    handleClearBearerTokenName,
    handleToggleActive,
    handleSave
  };
}
